import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SITE_NAV } from '@/config/siteNav';
import logo from '@/assets/svg/logo.svg';
import logoWhite from '@/assets/svg/logo-white.svg';
import { report, EVENTTYPES } from '@/utils/analytics';
import styles from './index.module.scss';

type HeaderProps = {
  /** 叠在头图上：未滚动透明，滚动后白底吸顶。 */
  overlay?: boolean;
  /** 深色头图：logo / 导航 / 下载按钮为白色。滚动白底后恢复默认。 */
  light?: boolean;
};

function navKeyFromPath(pathname: string) {
  if (
    pathname.startsWith('/aixue-ai') ||
    pathname.startsWith('/aixue-phonics') ||
    // pathname.startsWith('/aixue-xiabao') ||
    pathname.startsWith('/aixue-explain') ||
    pathname.startsWith('/aixue-guide')
  ) {
    return 'products';
  }
  if (pathname.startsWith('/aixue-about')) return 'about';
  if (pathname.startsWith('/aixue-join')) return 'careers';
  if (pathname.startsWith('/aixue-download')) return 'downloads';
  return 'home';
}

function scrollToTop() {
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
  window.scrollTo(0, 0);
}

export default function Header({ overlay = true, light = false }: HeaderProps) {
  const { pathname } = useLocation();
  const activeKey = navKeyFromPath(pathname || '');
  const [scrolled, setScrolled] = useState(false);
  const useLight = light && !(overlay && scrolled);

  useEffect(() => {
    if (!overlay) {
      return;
    }

    const onScroll = () => {
      const top = window.pageYOffset || document.documentElement.scrollTop || 0;
      setScrolled(top > 0);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, false);
    return () => {
      window.removeEventListener('scroll', onScroll, false);
    };
  }, [overlay]);

  const handleSamePageClick = (targetPath: string, onNavigate?: () => void) => () => {
    onNavigate?.();
    if (pathname === targetPath) {
      scrollToTop();
    }
  };

  const headerClass = [
    styles.header,
    overlay ? styles.overlay : '',
    overlay && scrolled ? styles.scrolled : '',
    useLight ? styles.light : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <header className={headerClass}>
      <div className={styles.inner}>
        <Link
          to="/"
          className={styles.logo}
          aria-label="爱学首页"
          onClick={handleSamePageClick('/')}
        >
          <img src={useLight ? logoWhite : logo} alt="爱学" />
        </Link>
        <nav className={styles.nav} aria-label="主导航">
          {SITE_NAV.map((item) => {
            const itemClass = `${styles.item} ${activeKey === item.key ? styles.active : ''}`;
            const onNavClick = () =>
              report({
                event_type: EVENTTYPES.MODULE_CLICK,
                event_id: 'website_tab_click',
                data: { button_name: item.label },
              });

            if (item.children && item.children.length > 0) {
              return (
                <div key={item.key} className={styles.menu}>
                  <span className={itemClass} tabIndex={0} aria-haspopup="true">
                    {item.label}
                  </span>
                  <div className={styles.dropdown}>
                    <div className={styles.dropdownPanel}>
                      {item.children.map((child) => (
                        <Link
                          key={child.key}
                          to={child.path}
                          className={`${styles.dropdownItem} ${
                            pathname === child.path ? styles.dropdownItemActive : ''
                          }`}
                          onClick={handleSamePageClick(child.path, () =>
                            report({
                              event_type: EVENTTYPES.MODULE_CLICK,
                              event_id: 'website_tab_click',
                              data: { button_name: child.label },
                            }),
                          )}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={item.key}
                to={item.path}
                className={itemClass}
                onClick={handleSamePageClick(item.path, onNavClick)}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
        <Link
          className={styles.cta}
          to="/aixue-download"
          onClick={handleSamePageClick('/aixue-download')}
        >
          下载中心
        </Link>
      </div>
    </header>
  );
}
